<template lang="pug">
div
  template(v-if="currentView === 'listing'")
    BlueSearch
      template(#default="{ reloading }")
        .schedule-app(:class="{ 'schedule-app--reloading': reloading }")
          //- Search bar + sort + filter dropdowns
          header.schedule-app__header
            ScheduleControls

          //- Active filter chips
          ScheduleFiltersTags

          //- Day tabs with action buttons
          DayTabs(
            @open-modal="openModal"
            @open-share="handleOpenShare"
            @print="onPrint")

          //- Session list
          section.schedule-app__body
            SearchResults
              template(#default="{ results }")
                .schedule-app__results(v-if="results.length")
                  template(v-for="item in buildRenderList(results)")
                    .schedule-day-separator(
                      v-if="item.type === 'separator'"
                      :key="'sep-' + item.dateValue") {{ item.dayLabel }}
                    ScheduleSessionCard(
                      v-else-if="item.isNonSessionItem"
                      :key="item.id"
                      :result="item.sessions[0]"
                      mode="simple"
                      :show-date="showDateOnCards"
                      @add-session="onAddSession"
                      @navigate-detail="onNavigateDetail")
                    ConcurrentSessionGroup(
                      v-else
                      :key="item.id"
                      :sessions="item.sessions"
                      :startTime="item.startTime"
                      :show-date="showDateOnCards"
                      :filter-reset-key="filterResetKey"
                      @add-session="onAddSession"
                      @navigate-detail="onNavigateDetail")
                .schedule-app__no-results(v-else-if="!reloading")
                  p No sessions match your current filters. Adjust your filters or select another day.

          //- Pagination
          footer.schedule-app__footer
            SearchPagination(v-if="totalPages > 1")

  SessionDetail(
    v-else
    :swapcard-session-id="currentSessionId"
    :parent-content-item-codename="$root.parentContentItemCodename"
    :parent-page-url="$root.parentPageUrl"
    :schedule-tab-codename="$root.tabKey || ''"
    @session-loaded="onSessionLoaded")

  //- Modals — always mounted so they can open from both listing and detail views
  SwapcardLoginModal(
    :visible="showLoginModal"
    :login-url="loginModalSessionUrl"
    @close="onModalClose")

  SharePopover(
    :visible="showSharePopover"
    :trigger-el="sharePopoverTriggerEl"
    @close="closeSharePopover")

  AddSessionPopover(
    :visible="showAddPopover"
    :trigger-el="addPopoverTriggerEl"
    :session-url="addPopoverSessionUrl"
    @close="closeAddPopover")

  //- Print-only root. Hoisted to <body> by mounted() so the body-level hide in
  //- _print.scss can isolate it. Always present in the DOM; hidden on screen.
  div.schedule-print-root(ref="printRoot")
    SchedulePrintView(
      v-if="printScope === 'all' || printScope === 'current'"
      :sessions="printSessions"
      :grouping-mode="printGroupingMode"
      :scope="printScope")
    SessionPrintView(
      v-else-if="printScope === 'detail'"
      :session="printSession")
</template>

<script>
import DayTabs from './DayTabs.vue';
import ConcurrentSessionGroup from './ConcurrentSessionGroup.vue';
import ScheduleSessionCard from './ScheduleSessionCard.vue';
import ScheduleControls from './ScheduleControls.vue';
import ScheduleFiltersTags from './ScheduleFiltersTags.vue';
import SwapcardLoginModal from './SwapcardLoginModal.vue';
import SharePopover from './SharePopover.vue';
import SessionDetail from './SessionDetail.vue';
import AddSessionPopover from './AddSessionPopover.vue';
import SchedulePrintView from './SchedulePrintView.vue';
import SessionPrintView from './SessionPrintView.vue';
import { scheduleEventBus, SCHEDULE_EVENTS } from './scheduleEventBus';
import { groupByStartTime as groupByStartTimeHelper, pickGroupingMode, buildRenderList as buildRenderListHelper } from './scheduleResultGrouping.js';

export default {
  name: 'ScheduleApp',

  components: {
    DayTabs,
    ConcurrentSessionGroup,
    ScheduleSessionCard,
    ScheduleControls,
    ScheduleFiltersTags,
    SwapcardLoginModal,
    SharePopover,
    SessionDetail,
    AddSessionPopover,
    SchedulePrintView,
    SessionPrintView,
  },

  computed: {
    totalPages() {
      return (this.$store.getters.results || {}).totalPages || 0;
    },

    isAllDays() {
      return !this.$store.state.selectedDayValue;
    },

    filterResetKey() {
      const results = this.$store.getters.results;
      const searchTerm = ((results && results.queryUsed) || {}).searchTerm || '';
      const filters = this.$store.getters.filters || [];
      const activeIds = filters
        .filter(g => g.id !== 'session_day')
        .flatMap(g => (g.filters || []).filter(f => f.selected).map(f => f.id))
        .sort()
        .join(',');
      const hasActive = !!(searchTerm || activeIds);
      return hasActive ? `${searchTerm}|${activeIds}` : '';
    },

    isTimeSort() {
      const sortOption = this.$store.state.search.sortOption;
      const searchTerm = (this.$store.getters.results.queryUsed || {}).searchTerm || '';
      return pickGroupingMode({ sortOption, searchTerm }) === 'time-grouped';
    },

    showDateOnCards() {
      return this.isAllDays && !this.isTimeSort;
    },
  },

  data() {
    return {
      showLoginModal: false,
      showSharePopover: false,
      sharePopoverTriggerEl: null,
      currentView: 'listing',
      currentSessionId: null,
      showAddPopover: false,
      addPopoverTriggerEl: null,
      addPopoverSessionUrl: '',
      loginModalSessionUrl: '',
      // Print-only render state. `null` when not printing.
      printScope: null,             // 'all' | 'current' | 'detail' | null
      printSessions: [],            // SchedulePrintView input
      printGroupingMode: 'time-grouped',
      printSession: null,           // SessionPrintView input
    };
  },

  mounted() {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    const contentTypeFilter = this.config.contentTypeFilter;
    if (contentTypeFilter) {
      this.$store.commit('extend', (ext) => ({
        ...ext,
        contentTypeFilter,
      }));
    }

    if (this.$root.mode === 'detail' && this.$root.swapcardSessionId) {
      this.currentView = 'detail';
      this.currentSessionId = this.$root.swapcardSessionId;
    }

    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('afterprint', this.resetPrintState);
    scheduleEventBus.$on(SCHEDULE_EVENTS.OPEN_MODAL, this.handleOpenModal);
    scheduleEventBus.$on(SCHEDULE_EVENTS.OPEN_SHARE, this.handleOpenShare);
    scheduleEventBus.$on(SCHEDULE_EVENTS.PRINT_SCHEDULE, this.handlePrint);
    scheduleEventBus.$on(SCHEDULE_EVENTS.PRINT_SESSION, this.handlePrintSession);
    scheduleEventBus.$on(SCHEDULE_EVENTS.NAVIGATE_LISTING, this.handleNavigateListing);
    this.$nextTick(() => this.hoistPrintRoot());
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('afterprint', this.resetPrintState);
    scheduleEventBus.$off(SCHEDULE_EVENTS.OPEN_MODAL, this.handleOpenModal);
    scheduleEventBus.$off(SCHEDULE_EVENTS.OPEN_SHARE, this.handleOpenShare);
    scheduleEventBus.$off(SCHEDULE_EVENTS.PRINT_SCHEDULE, this.handlePrint);
    scheduleEventBus.$off(SCHEDULE_EVENTS.PRINT_SESSION, this.handlePrintSession);
    scheduleEventBus.$off(SCHEDULE_EVENTS.NAVIGATE_LISTING, this.handleNavigateListing);
    this.unhoistPrintRoot();
  },

  methods: {
    handlePopState() {
      const path = window.location.pathname;
      if (!path.includes('/sessions/')) {
        this.currentView = 'listing';
        this.currentSessionId = null;
        this.$nextTick(() => requestAnimationFrame(this.scrollToTop));
      }
    },

    onNavigateDetail({ swapcardSessionId }) {
      this.currentSessionId = swapcardSessionId;
      this.currentView = 'detail';
      this.$nextTick(() => requestAnimationFrame(this.scrollToTop));
    },

    onSessionLoaded() {
      this.$nextTick(() => requestAnimationFrame(this.scrollToTop));
    },

    handleNavigateListing() {
      this.currentView = 'listing';
      this.currentSessionId = null;
      this.$nextTick(() => requestAnimationFrame(this.scrollToTop));
    },

    scrollToTop() {
      const top = Math.round(this.$el.getBoundingClientRect().top + window.scrollY - 100); // Less some top padding
      if (window.pageHeader?.scrollTo) {
        window.pageHeader.scrollTo(top, { nav: 'hide', behavior: 'instant' });
      } else {
        this.$el.scrollIntoView({ behavior: 'instant' });
      }
    },

    /**
     * Group sessions by start time when an explicit time sort is active,
     * or when relevance sort is selected with no search term (the backend
     * fulfils relevance+no-term with event_start_date order anyway).
     * Alphabetical and date sorts suppress grouping regardless of search term.
     */
    groupByStartTime(results) {
      const sortOption = this.$store.state.search.sortOption;
      const searchTerm = (this.$store.getters.results.queryUsed || {}).searchTerm || '';
      return groupByStartTimeHelper(results, { sortOption, searchTerm });
    },

    buildRenderList(results) {
      const groups = this.groupByStartTime(results);
      return buildRenderListHelper(groups, this.isAllDays, this.isTimeSort);
    },

    openModal(source) {
      scheduleEventBus.$emit(SCHEDULE_EVENTS.OPEN_MODAL, { source });
    },

    onAddSession({ result, triggerEl }) {
      this.addPopoverTriggerEl = triggerEl;
      this.addPopoverSessionUrl = (result && result.meta && result.meta.sessionUrl) || '';
      this.showAddPopover = true;
    },

    closeAddPopover() {
      this.showAddPopover = false;
      this.addPopoverTriggerEl = null;
      this.addPopoverSessionUrl = '';
    },

    handleOpenModal(payload) {
      this.loginModalSessionUrl = (payload && payload.sessionUrl) || '';
      this.showLoginModal = true;
    },

    onModalClose() {
      this.showLoginModal = false;
      this.loginModalSessionUrl = '';
    },

    handleOpenShare({ triggerEl } = {}) {
      this.sharePopoverTriggerEl = triggerEl || null;
      this.showSharePopover = true;
    },

    closeSharePopover() {
      this.showSharePopover = false;
      this.sharePopoverTriggerEl = null;
    },

    /**
     * DayTabs `@print="onPrint"` binding — invoked with the chosen scope from
     * the print dropdown ('all' or 'current').
     */
    onPrint(scope) {
      this.startPrintAgenda(scope);
    },

    /**
     * Bus handler for PRINT_SCHEDULE. The DayTabs path drives `onPrint`
     * directly, but external callers (e.g. SessionDetailSidebar legacy paths,
     * keyboard shortcuts) may still emit PRINT_SCHEDULE; default to 'current'.
     */
    handlePrint(payload) {
      const scope = (payload && payload.scope) || 'current';
      this.startPrintAgenda(scope);
    },

    /**
     * Detail-print bus handler. Renders SessionPrintView for the supplied session.
     */
    handlePrintSession({ session } = {}) {
      if (!session) return;
      this.printSession = session;
      this.printScope = 'detail';
      this.$nextTick(() => { window.print(); });
    },

    /**
     * Fetch the print session list, decide grouping, then trigger window.print().
     * `printScope` is set last so the print-only template is rendered with
     * fully-populated data on the next tick.
     */
    async startPrintAgenda(scope) {
      this.printScope = null;
      this.printSession = null;
      this.printSessions = await this.$store.dispatch('fetchAllForPrint', { scope });
      if (scope === 'all') {
        this.printGroupingMode = 'time-grouped';
      } else {
        const sortOption = this.$store.state.search.sortOption;
        const searchTerm = (this.$store.getters.results.queryUsed || {}).searchTerm || '';
        this.printGroupingMode = pickGroupingMode({ sortOption, searchTerm });
      }
      this.printScope = scope;
      this.$nextTick(() => { window.print(); });
    },

    /**
     * `afterprint` cleanup — also called if the user cancels the print dialog.
     */
    resetPrintState() {
      this.printScope = null;
      this.printSession = null;
      this.printSessions = [];
    },

    /**
     * Move `.schedule-print-root` to be a direct child of <body>.
     * Required so the body-level hide in `_print.scss`
     * (`body > *:not(.schedule-print-root) { display: none }`) can isolate
     * print output. Vue 2 patches the host element via vdom regardless of
     * where it sits in the DOM, so moving it does not affect reactivity.
     */
    hoistPrintRoot() {
      const el = this.$refs.printRoot;
      if (el && el.parentNode !== document.body) {
        document.body.appendChild(el);
      }
    },

    /**
     * Remove the hoisted print root on unmount so hot reloads or SPA
     * navigation do not leave orphaned nodes attached to <body>.
     */
    unhoistPrintRoot() {
      const el = this.$refs.printRoot;
      if (el && el.parentNode === document.body) {
        document.body.removeChild(el);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule-app {
  padding: 20px;
  // background: #F9F9FA;
  font-family: 'Public Sans', sans-serif;
  width: 100%;

  @media (max-width: 991px) {
    padding: 0;
  }

  &--reloading {
    opacity: 0.6;
    pointer-events: none;
  }
}

.schedule-app__header {
  margin-bottom: 0;
}

.schedule-app__body {
  margin-bottom: 2rem;
}

.schedule-app__no-results {
  text-align: center;
  padding: 3rem;
  color: #9E9E9E;
  font-size: 16px;
}

.schedule-app__footer {
  margin-top: 1rem;
}

.schedule-day-separator {
  background: #163874;
  color: white;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Public Sans', sans-serif;
  border-radius: 6px;
  margin-bottom: 12px;
}
</style>

<!-- Global (unscoped) print rules. Must NOT be scoped — scoped data-v-* attribute
     selectors would not match the .schedule-print-root container, and we need to
     hide elements outside the schedule app on print. -->
<style lang="scss">
@import './_print.scss';
</style>
